import { Icons } from '@/components/icons';
import TicketCuticleLine from '@/components/manage/TicketCuticleLine';
import LegDate from '@/components/manage/tickets/LegDate';
import TransportBadge from '@/components/manage/tickets/TransportBadge';
import TripDetails from '@/components/manage/tickets/TripDetails';
import { Button } from '@/components/primitives/Button';
import { Typography } from '@/components/primitives/Typography';
import { useSwipe } from '@/hooks/useSwipe';
import { TransText } from '@/i18n/trans/text';
import type { AdmissionDTO, LegDTO } from '@/types/dto/agent';
import type { CheckoutOrBookingPassengerMap } from '@/types/passenger';
import { areRefundedLike } from '@/utils/admission';
import { cn } from '@/utils/cn';
import { getPassengerName } from '@/utils/passenger';
import bwipjs from '@bwip-js/browser';
import { useState, type FC } from 'react';

interface ParsedAdmission extends Omit<AdmissionDTO, 'passengerIds'> {
  passengerId: NonNullable<AdmissionDTO['passengerIds']>[number];
}

interface TicketViewerContentProps {
  leg: LegDTO;
  admissions: Array<AdmissionDTO>;
  passengersMap: CheckoutOrBookingPassengerMap;
}

const TicketViewerContent: FC<TicketViewerContentProps> = ({
  leg,
  admissions,
  passengersMap,
}) => {
  const parsedAdmissions = admissions.reduce((acc, admission) => {
    for (const passengerId of admission.passengerIds ?? []) {
      acc.push({
        ...admission,
        passengerId,
      });
    }

    return acc;
  }, [] as Array<ParsedAdmission>);

  const [selectedIndex, setSelectedIndex] = useState(0);

  const currentAdmission = parsedAdmissions[selectedIndex];
  const controlNumber = currentAdmission.fulfillments?.[0].controlNumber ?? '';
  const passenger = passengersMap[currentAdmission.passengerId];
  const passengerName = getPassengerName(passenger);
  const refundedLike = areRefundedLike(admissions);

  const handleSelectLeft = () => {
    if (selectedIndex > 0) {
      setSelectedIndex(selectedIndex - 1);
    }
  };

  const handleSelectRight = () => {
    if (selectedIndex < parsedAdmissions.length - 1) {
      setSelectedIndex(selectedIndex + 1);
    }
  };

  const swipeHandlers = useSwipe(handleSelectRight, handleSelectLeft);

  return (
    <div className="flex min-h-full flex-col" {...swipeHandlers}>
      <div className="flex items-center justify-between border-y border-primary bg-primary-light px-6 py-3">
        <Button
          data-testid="ticket-viewer-back"
          size="small"
          variant="ghost"
          className={cn({
            'pointer-events-none opacity-0': selectedIndex === 0,
          })}
          onClick={handleSelectLeft}
        >
          <Icons.arrowLeft width={16} height={16} />
          <TransText i18nKey="back" />
        </Button>
        <Typography variant="body1-bold" className="mx-auto text-primary">
          {passengerName}
        </Typography>
        <Button
          data-testid="ticket-viewer-next"
          size="small"
          variant="ghost"
          className={cn({
            'pointer-events-none opacity-0':
              selectedIndex === parsedAdmissions.length - 1,
          })}
          onClick={handleSelectRight}
        >
          <TransText i18nKey="next" />
          <Icons.arrowRight width={16} height={16} />
        </Button>
      </div>
      <div className="mt-2 flex justify-center gap-3">
        {parsedAdmissions.map(({ passengerId }, index) => (
          <div
            key={passengerId}
            className={cn('h-2 w-2 rounded-full border border-primary', {
              'bg-primary': index === selectedIndex,
            })}
          />
        ))}
      </div>
      <div className="mx-6 flex flex-col overflow-x-hidden py-4">
        <div className="flex gap-3">
          <div className="flex flex-1 flex-col gap-1">
            <Typography variant="body2" className="text-neutral">
              <TransText i18nKey="passenger" />
            </Typography>
            <Typography variant="body1-bold" className="text-primary">
              {passengerName}
            </Typography>
          </div>
          <canvas
            ref={(canvas) => {
              if (!canvas) {
                return;
              }

              bwipjs.toCanvas(canvas, {
                bcid: 'azteccode',
                text: controlNumber,
              });
            }}
            className="flex h-[144px] w-[144px] items-center justify-center"
          />
        </div>
        <TicketCuticleLine className="my-4 w-[calc(100%+48px)] -translate-x-2" />
        <div className="align-center flex justify-between">
          <TransportBadge leg={leg} refundedLike={refundedLike} />
          <LegDate date={leg.departureTime} />
        </div>
        <TripDetails leg={leg} className="mt-4" />
        <div className="mt-6 flex flex-col gap-3 rounded bg-primary-pale py-3 pl-4 pr-14">
          <div className="flex justify-between">
            <div className="flex items-center gap-2">
              <Icons.ticket className="h-4 w-4" />
              <Typography variant="body2-bold" className="text-dark">
                {currentAdmission.productDescription}
              </Typography>
            </div>
            <div className="flex flex-col gap-1">
              {currentAdmission.reservations?.map((reservation, key) => {
                const reservedPlace =
                  reservation.placeAllocations?.reservedPlaces?.[0];

                if (!reservedPlace) {
                  return null;
                }

                return (
                  <div
                    className="flex min-w-20 gap-3"
                    key={`${reservation.id}-${key}`}
                  >
                    {leg.ptMode?.code !== 'BUS' &&
                      reservedPlace?.coachNumber && (
                        <div className="flex items-center gap-1.5">
                          <Icons.carriage className="h-3 w-3 text-primary" />
                          <Typography
                            variant="body2-bold"
                            className="text-dark"
                          >
                            {reservedPlace.coachNumber}
                          </Typography>
                        </div>
                      )}
                    {reservedPlace?.placeNumber && (
                      <div className="flex items-center gap-1.5">
                        <Icons.seat className="h-3 w-3 text-primary" />
                        <Typography variant="body2-bold" className="text-dark">
                          {reservedPlace.placeNumber}
                        </Typography>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          {currentAdmission.ancillaries?.map((ancillary, key) => (
            <div
              key={`${ancillary.id}-${key}`}
              className="flex items-center gap-2"
            >
              <Icons.addon className="h-4 w-4" />
              <Typography variant="body2-bold" className="text-dark">
                {ancillary.productDescription}
              </Typography>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default TicketViewerContent;
